import type { ReactNode } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';
import type { IconButtonProps } from '@mui/material';

import Portal from '@mui/material/Portal';

import { useNotificationAvoid } from '../../components/notification-banner/avoid-registry';
import { Stack, Button, Tooltip, IconButton, Typography } from '@mui/material';

export type SelectionAction = {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  testId?: string;
};

type BucketActions = {
  inBin: boolean;
  download: SelectionAction;
  restore: SelectionAction;
  purge: SelectionAction;
  remove: SelectionAction;
  /** Only a library with folders offers this. */
  move?: SelectionAction;
};

/** Download first. The bin offers Restore and Delete permanently. Every other view offers Delete, after Move. */
export function bucketSelectionActions({
  inBin,
  download,
  restore,
  purge,
  remove,
  move,
}: BucketActions): SelectionAction[] {
  if (inBin) return [download, restore, { ...purge, destructive: true }];
  return [download, ...(move ? [move] : []), { ...remove, destructive: true }];
}

type SelectionIconActionProps = {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  color?: IconButtonProps['color'];
  sx?: SxProps<Theme>;
  testId?: string;
};

/** An icon-only action. The label stays its name and its tooltip. */
export function SelectionIconAction({ label, icon, onClick, disabled, color, sx, testId }: SelectionIconActionProps) {
  return (
    <Tooltip title={label}>
      {/* A disabled button fires no events, and a tooltip needs something that does. */}
      <span>
        <IconButton
          aria-label={label}
          size="small"
          color={color}
          disabled={disabled}
          onClick={onClick}
          sx={sx}
          data-testid={testId}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}

// On a phone the hit targets grow to a finger-sized 48px. A mouse keeps the compact size.
const SELECTION_ICON_BUTTON_SX = {
  color: 'common.white',
  width: { xs: 48, sm: 34 },
  height: { xs: 48, sm: 34 },
  '& svg': { width: { xs: 22, sm: 16 }, height: { xs: 22, sm: 16 } },
};

const SELECTION_DELETE_BUTTON_SX = {
  ...SELECTION_ICON_BUTTON_SX,
  bgcolor: 'error.main',
  '&:hover': { bgcolor: 'error.dark' },
};

type SelectionBarProps = {
  testId: string;
  /** What the count says, such as "3 selected". */
  countLabel: string;
  selectAllLabel: string;
  onSelectAll: () => void;
  actions: SelectionAction[];
  cancelLabel: string;
  cancelIcon: ReactNode;
  onCancel: () => void;
};

/** The floating bar over a selection. A Portal, because the page transition wrapper animates transform
 * and filter, and either one traps a position:fixed descendant inside the page. */
export function SelectionBar({
  testId,
  countLabel,
  selectAllLabel,
  onSelectAll,
  actions,
  cancelLabel,
  cancelIcon,
  onCancel,
}: SelectionBarProps) {
  const avoidRef = useNotificationAvoid();

  return (
    <Portal>
      <Stack
        ref={avoidRef}
        data-testid={testId}
        direction="row"
        // useFlexGap so the row gap applies once the bar wraps.
        useFlexGap
        spacing={1}
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 48,
          transform: 'translateX(-50%)',
          zIndex: (theme) => theme.zIndex.appBar,
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'center',
          // Spans the viewport on phones, so the actions get the widest line available.
          width: { xs: 'calc(100vw - 16px)', sm: 'auto' },
          maxWidth: 'calc(100vw - 16px)',
          borderRadius: 2,
          bgcolor: 'container.low',
          boxShadow: 8,
          px: { xs: 1, sm: 2 },
          py: { xs: 1.5, sm: 1 },
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          // Full width on phones, so the count takes its own row.
          sx={{
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            px: 0.5,
            width: { xs: '100%', sm: 'auto' },
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          {countLabel}
        </Typography>
        {/* nowrap: the actions are one unit and never split across rows. */}
        <Stack
          direction="row"
          useFlexGap
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'center', flexWrap: 'nowrap' }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={onSelectAll}
            sx={{ minHeight: { xs: 44, sm: 'auto' }, px: 1.5, whiteSpace: 'nowrap' }}
          >
            {selectAllLabel}
          </Button>
          {actions.map((action) => (
            <SelectionIconAction
              key={action.key}
              label={action.label}
              icon={action.icon}
              disabled={action.disabled}
              color={action.destructive ? 'error' : undefined}
              onClick={action.onClick}
              sx={action.destructive ? SELECTION_DELETE_BUTTON_SX : SELECTION_ICON_BUTTON_SX}
              testId={action.testId}
            />
          ))}
          <IconButton aria-label={cancelLabel} size="small" onClick={onCancel} sx={SELECTION_ICON_BUTTON_SX}>
            {cancelIcon}
          </IconButton>
        </Stack>
      </Stack>
    </Portal>
  );
}
